import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Search, FileText } from "lucide-react";

export default function COALibrary() {
  const [coas, setCoas] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/coa").then(({ data }) => setCoas(data)).catch(() => {}); }, []);

  const filtered = coas.filter((c) => {
    const ql = q.toLowerCase();
    return !ql || c.batch?.toLowerCase().includes(ql) || c.coa_id?.toLowerCase().includes(ql) || c.sku?.toLowerCase().includes(ql) || c.peptide_slug?.toLowerCase().includes(ql);
  });

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto">
      <div className="mono-label mb-3">Batch testing</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-white mb-4">COA Library</h1>
      <p className="text-slate-400 text-sm mb-8 max-w-xl">Every batch tested. Every result public. Search by peptide, SKU, batch, lot or COA ID.</p>

      <div className="relative max-w-lg mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search batch, SKU, COA ID…" data-testid="coa-search-input"
          className="pl-9 bg-black/40 border-white/10 font-mono" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <div key={c.id} className="glass rounded-2xl p-5" data-testid={`coa-card-${c.batch}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                <FileText className="text-cyan-300" size={16} />
              </div>
              {c.demo && <span className="px-2 py-0.5 rounded-full bg-violet-500/15 border border-violet-400/30 text-[9px] font-mono uppercase tracking-widest text-violet-300">DEMO</span>}
            </div>
            <div className="font-mono text-sm text-white">{c.batch}</div>
            <div className="text-[11px] font-mono text-slate-500 mt-1">{c.peptide_slug} · {c.sku}</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-mono">
              <div className="rounded-lg border border-white/5 p-2"><div className="text-slate-500 text-[9px] uppercase">Purity</div><div className="text-cyan-300">{c.purity_pct}%</div></div>
              <div className="rounded-lg border border-white/5 p-2"><div className="text-slate-500 text-[9px] uppercase">HPLC</div><div className="text-emerald-400">{c.hplc}</div></div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-[10px] font-mono text-slate-500">{c.laboratory}</span>
              <Link to={`/verify/${c.batch}`} className="text-xs font-mono text-cyan-300 flex items-center gap-1"><ShieldCheck size={12} /> Verify</Link>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="glass rounded-2xl p-12 text-center" data-testid="coa-empty">
          <FileText className="mx-auto text-slate-600 mb-4" size={32} />
          <div className="text-white font-serif text-xl mb-2">No certificates published yet</div>
          <div className="text-slate-500 text-sm max-w-md mx-auto">Verified Certificates of Analysis for each batch will appear here as they are released. Check back soon.</div>
        </div>
      )}
    </div>
  );
}
